import { parseChecklistDueDate } from "@smartsuite/react-ui/lib/components/fields/Checklist/ChecklistDueDate/ChecklistDueDate.helpers";
import type { ChecklistAssigneeType, ChecklistItem, ID } from "@smartsuite/types";
import { format } from "date-fns";

import { smartdocToReviewString } from "../../review/reviewString.helpers";

/** The format the review page already uses for a date with no time part. */
const REVIEW_DATE_FORMAT = "MMMM dd, yyyy";

/**
 * A checklist item's due date is a day, not an instant: it is stored as the picked day's
 * wall-clock midnight stamped with a literal `Z` (pinned in `checklistDueDate.test.ts`). Reading
 * it with `new Date()` the way the other review values do would shift the day for every
 * respondent whose offset is negative, so react-ui's own parser resolves it first.
 */
export const formatChecklistDueDate = (dueDate?: string | null): string => {
  if (!dueDate) return "";

  try {
    const parsed = parseChecklistDueDate(dueDate);
    if (Number.isNaN(parsed.getTime())) return "";

    return format(parsed, REVIEW_DATE_FORMAT);
  } catch {
    console.warn("Unable to format a checklist due date for the review page", dueDate);
    return "";
  }
};

export interface ChecklistReviewRow {
  assigneeName: string;
  completed: boolean;
  dueDate: string;
  id: string;
  text: string;
}

export interface BuildChecklistReviewRowsArgs {
  items: ChecklistItem[];
  isAssigneeEnabled: boolean;
  isDueDateEnabled: boolean;
  getAssigneeName: (assignee?: ID | null, assigneeType?: ChecklistAssigneeType | null) => string;
}

/**
 * One row per item, in the order the respondent left them. Assignee and due date are shown for
 * every item that carries one — not only the completed ones — and each is dropped when the form
 * has that Checklist Option switched off, so the summary shows exactly the columns the form did.
 *
 * Items with no text are dropped: they cannot be told apart visually and the single-line
 * `checklistToReviewString`, which still decides answered from unanswered, ignores them too.
 */
export const buildChecklistReviewRows = ({
  items,
  isAssigneeEnabled,
  isDueDateEnabled,
  getAssigneeName,
}: BuildChecklistReviewRowsArgs): ChecklistReviewRow[] =>
  items
    .map((item) => ({
      assigneeName: isAssigneeEnabled ? getAssigneeName(item.assignee, item.assignee_type) : "",
      completed: !!item.completed,
      dueDate: isDueDateEnabled ? formatChecklistDueDate(item.due_date) : "",
      id: item.id,
      text: smartdocToReviewString(item.content),
    }))
    .filter((row) => !!row.text);
